/**
 * Session Transformer
 * Transforms API data to UI models
 */

import { ChargeSessionApiModel, SessionBreakdownResponse } from '../types/api-types';
import { SessionGridRow, SessionDetail } from '../types/session-types';

export class SessionTransformer {
  /**
   * Transform API ChargeSessionModel to UI SessionGridRow
   * Maps Flutter field names to API fields
   */
  static toGridRow(apiModel: ChargeSessionApiModel): SessionGridRow {
    // Settlement value is the total payment including ALL fees, VAT, and after discounts
    // This matches the backend's TotalPaymentAfterDiscountIncVat field
    const settlementValue = apiModel.totalPaymentAfterDiscountIncVat;

    return {
      // Flutter field mappings
      locationId: apiModel.actorId,
      sessionId: apiModel.chargeSessionId,
      kioskActorHierarchy: apiModel.actorHierarchy || '',
      locationName: apiModel.machineName || '',
      startTime: apiModel.deviceLocalStartDate || '',
      stopTime: apiModel.deviceLocalStopDate,
      totalEnergy: apiModel.totalEnergy,
      settlementValue: settlementValue || null,
      isVirtualSession: apiModel.isVirtualSession ?? false,

      // Extra fields for detail
      machineId: apiModel.machineId,
      connectorId: apiModel.connectorId,
      paymentId: apiModel.paymentId,
      durationInSeconds: apiModel.durationInSeconds,
      currencyName: apiModel.currencyName,
      cost: apiModel.cost,
      vatCost: apiModel.vatCost,

      // Additional fields for extended grid columns
      meterStart: apiModel.meterStart,
      meterStop: apiModel.meterStop,
      stopReason: apiModel.stopReason,
      startSource: apiModel.startSource,
      accountName: apiModel.accountName,
      cardType: apiModel.cardType,
      rfid: apiModel.rfid,

      // Fee breakdowns
      connectionFee: apiModel.connectionFee,
      connectionFeeWithoutVat: apiModel.connectionFeeWithoutVat,
      connectionFeeVat: apiModel.connectionFeeVat,
      timeFee: apiModel.timeFee,
      timeFeeWithoutVat: apiModel.timeFeeWithoutVat,
      timeFeeVat: apiModel.timeFeeVat,
      idleFee: apiModel.idleFee,
      idleFeeWithoutVat: apiModel.idleFeeWithoutVat,
      idleFeeVat: apiModel.idleFeeVat,

      // Total payment breakdown
      totalPaymentCostExcVat: apiModel.totalPaymentCostExcVat,
      totalPaymentVat: apiModel.totalPaymentVat,
      totalPaymentCostIncVat: apiModel.totalPaymentCostIncVat,

      vatRate: apiModel.vatRate,
      machineUsage: apiModel.machineUsage,
      physicalReference: apiModel.physicalReference,
      deviceSerial: apiModel.deviceSerial,
      machineModelName: apiModel.machineModelName,  // HIVE-6618: Device type
      isOcpi: apiModel.isOcpi,
      ocpiEmspName: apiModel.ocpiEmspName,

      // NEW columns (from Figma/Flutter)
      nayaxTransactionId: apiModel.nayaxTransactionId,
      evTierPricing: apiModel.evTierPricing, // HIVE-5935: Tariff tier pricing (e.g., "שפל\פסגה")
      accountExternalCode: apiModel.externalCode,
      isExternalPayment: apiModel.isExternalPayment ?? null,
      tokenId: apiModel.idToken,
      tokenDisplayNumber: apiModel.rfid, // Same as rfid, aliased for clarity
      sparkMachineId: apiModel.sparkMachineId,
      deviceNumber: apiModel.deviceSerial, // Aliased
      prepaidCard: apiModel.prepaidCardDisplayNumber, // Prepaid card display number
      chargePointId: apiModel.machineId, // Aliased
      chargePointNumber: apiModel.machineOperatorIdentifier,
      accountId: apiModel.accountId,

      // Community/Discount fields
      communityName: apiModel.communityName,
      communityValue: apiModel.communityValue,

      // Discount amounts (how much is saved)
      discountExcVat: apiModel.discountExcVat,
      discountVat: apiModel.discountVat,
      discountIncVat: apiModel.discountIncVat,

      // After-discount payment amounts
      totalPaymentAfterDiscountExcVat: apiModel.totalPaymentAfterDiscountExcVat,
      totalPaymentAfterDiscountVat: apiModel.totalPaymentAfterDiscountVat,
      totalPaymentAfterDiscountIncVat: apiModel.totalPaymentAfterDiscountIncVat,

      // Buy Info fields (HIVE-6234: matching Flutter)
      energyBuyCost: apiModel.energyBuyCost,
      energyProfit: apiModel.energyProfit,
      totalProfit: apiModel.totalProfit,

      // Tariff levels (L1-L9) - for Fees checkbox display like Flutter
      l1UnitCost: apiModel.l1UnitCost,
      l1Energy: apiModel.l1Energy,
      l1Cost: apiModel.l1Cost,
      l2UnitCost: apiModel.l2UnitCost,
      l2Energy: apiModel.l2Energy,
      l2Cost: apiModel.l2Cost,
      l3UnitCost: apiModel.l3UnitCost,
      l3Energy: apiModel.l3Energy,
      l3Cost: apiModel.l3Cost,
      l4UnitCost: apiModel.l4UnitCost,
      l4Energy: apiModel.l4Energy,
      l4Cost: apiModel.l4Cost,
      l5UnitCost: apiModel.l5UnitCost,
      l5Energy: apiModel.l5Energy,
      l5Cost: apiModel.l5Cost,
      l6UnitCost: apiModel.l6UnitCost,
      l6Energy: apiModel.l6Energy,
      l6Cost: apiModel.l6Cost,
      l7UnitCost: apiModel.l7UnitCost,
      l7Energy: apiModel.l7Energy,
      l7Cost: apiModel.l7Cost,
      l8UnitCost: apiModel.l8UnitCost,
      l8Energy: apiModel.l8Energy,
      l8Cost: apiModel.l8Cost,
      l9UnitCost: apiModel.l9UnitCost,
      l9Energy: apiModel.l9Energy,
      l9Cost: apiModel.l9Cost,
    };
  }

  /**
   * Transform API ChargeSessionModel to UI SessionDetail
   * Includes all fields for the detail blade
   */
  static toDetail(
    apiModel: ChargeSessionApiModel,
    breakdown?: SessionBreakdownResponse
  ): SessionDetail {
    const gridRow = this.toGridRow(apiModel);

    // Extract tariff info from breakdown if available
    const firstTariff = breakdown?.calcMeterDetails?.[0];
    const breakdownItems = breakdown?.details?.breakDowns;

    return {
      ...gridRow,

      // Charging
      connectorType: undefined, // Not available in main API
      updated: apiModel.updated || '',

      // Breakdown items from sessionbreakdown (completed sessions)
      breakdownItems: breakdownItems && breakdownItems.length > 0 ? breakdownItems : undefined,

      // Tariff from breakdown (legacy)
      tariffId: firstTariff?.tariffId,
      tariffDescription: firstTariff?.description,
      priceComponents: firstTariff?.unitPrice,
      validFrom: firstTariff?.validFrom,
      validTo: firstTariff?.validTo,
      restrictions: firstTariff?.restrictions,

      // Extra API fields
      meterStart: apiModel.meterStart,
      meterStop: apiModel.meterStop,
      connectionFee: apiModel.connectionFee,
      timeFee: apiModel.timeFee,
      idleFee: apiModel.idleFee,
      stopReason: apiModel.stopReason,
      calculationMethod: apiModel.calculationMethod,
      accountName: apiModel.accountName,
      vatRate: apiModel.vatRate,

      // Total payment breakdown (Energy Fee)
      totalPaymentCostExcVat: apiModel.totalPaymentCostExcVat,
      totalPaymentVat: apiModel.totalPaymentVat,
      totalPaymentCostIncVat: apiModel.totalPaymentCostIncVat,

      // L1-L9 tariff breakdown
      l1UnitCost: apiModel.l1UnitCost,
      l1Energy: apiModel.l1Energy,
      l1Cost: apiModel.l1Cost,
      l2UnitCost: apiModel.l2UnitCost,
      l2Energy: apiModel.l2Energy,
      l2Cost: apiModel.l2Cost,
      l3UnitCost: apiModel.l3UnitCost,
      l3Energy: apiModel.l3Energy,
      l3Cost: apiModel.l3Cost,
      l4UnitCost: apiModel.l4UnitCost,
      l4Energy: apiModel.l4Energy,
      l4Cost: apiModel.l4Cost,
      l5UnitCost: apiModel.l5UnitCost,
      l5Energy: apiModel.l5Energy,
      l5Cost: apiModel.l5Cost,
      l6UnitCost: apiModel.l6UnitCost,
      l6Energy: apiModel.l6Energy,
      l6Cost: apiModel.l6Cost,
      l7UnitCost: apiModel.l7UnitCost,
      l7Energy: apiModel.l7Energy,
      l7Cost: apiModel.l7Cost,
      l8UnitCost: apiModel.l8UnitCost,
      l8Energy: apiModel.l8Energy,
      l8Cost: apiModel.l8Cost,
      l9UnitCost: apiModel.l9UnitCost,
      l9Energy: apiModel.l9Energy,
      l9Cost: apiModel.l9Cost,
    };
  }

  /**
   * Transform array of API models to grid rows
   */
  static toGridRows(apiModels: ChargeSessionApiModel[]): SessionGridRow[] {
    return apiModels.map((model) => this.toGridRow(model));
  }

  /**
   * Format duration from seconds to HH:MM:SS string
   */
  static formatDuration(durationInSeconds: number | null): string {
    if (durationInSeconds === null || durationInSeconds === undefined) {
      return '--:--:--';
    }

    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = durationInSeconds % 60;

    return [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      seconds.toString().padStart(2, '0'),
    ].join(':');
  }

  /**
   * Format energy value
   */
  static formatEnergy(energy: number | null): string {
    if (energy === null || energy === undefined) {
      return 'N/A';
    }
    return `${energy.toFixed(2)} kWh`;
  }

  /**
   * Format currency value
   */
  static formatCurrency(
    value: number | null,
    currencyName: string | null = null
  ): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    // Determine decimal places based on currency (USD uses 4, others use 2)
    const decimals = currencyName?.toUpperCase() === 'USD' ? 4 : 2;
    const formatted = value.toFixed(decimals);

    if (currencyName) {
      return `${formatted} ${currencyName}`;
    }

    return formatted;
  }
}
